import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { VaultOrder, VaultSortBy } from '../../types/vault.types';
import { useAppQuery } from '../use-app-query.hook';
import { GetRepositoryResponse } from './use-repository-folders';

export const useOpenRepository = ({
  companyId,
  numberOfFetchedItems = 100,
  currentPage,
  search,
  sortBy,
  orderBy,
  enabled,
}: {
  companyId: string;
  currentPage: number;
  enabled?: boolean;
  numberOfFetchedItems?: number;
  orderBy?: VaultOrder;
  search: string;
  sortBy?: VaultSortBy;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const {
    data: { data, totalPages } = {},
    isLoading,
    refetch,
  } = useAppQuery<GetRepositoryResponse, HttpException>({
    queryKey: [
      QueryKey.GET_OPEN_REPOSITORY,
      companyId,
      {
        page: currentPage,
        skip,
        numberOfFetchedItems,
        sortBy,
        orderBy,
      },
    ],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'vault', 'open-folders'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip,
            search,
            ...(orderBy && sortBy ? { sortBy, orderBy } : {}),
          },
        },
      ),
    enabled: !!enabled && !!companyId,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_OPEN_REPOSITORY] });
  }, [queryClient]);

  if (!enabled) {
    return {
      invalidateQuery: () => {},
      folders: [],
      totalPages: 0,
      isLoading: false,
      refetch: () => {},
    };
  }

  return {
    invalidateQuery,
    folders: data,
    totalPages,
    isLoading,
    refetch,
  };
};
